// Landing-URL resolution for the role grids + Build View. Mirrors the
// row editor's pattern: master Overview selection (landingPageType +
// customLandingPageUrl) + per-platform override + per-location
// override + brand landingPageTemplate, flattened down to a single
// URL or null.
//
// Resolution chain (from highest to lowest precedence):
//   1. Row's customLandingPageUrl when landingPageType === 'custom'
//      OR a per-platform override carries `type: 'custom'` with a
//      non-empty URL.
//   2. Per-location override (FranchiseLocationProfile.{offer|sale
//      |location}LandingPage) when the effective type is offer / sale
//      / location AND the matching field is non-empty. Polish round 5
//      (null=inherit) inserted this layer; null and whitespace-only
//      strings fall through to step 3.
//   3. Brand template (FranchiseProfile.landingPageTemplate.{type})
//      with placeholder substitution applied at the call site.
//   4. Otherwise null.
//
// Steps 1-3 live inside modules/campaigns/landingPage.ts's resolveType
// so the master path AND the per-platform-override path both benefit
// from the LP precedence without duplication.
//
// Pure: no I/O, no DOM. Returns the literal string at whichever layer
// resolved (which still carries {placeholder} tokens when it came from
// step 3). Callers pass the result through renderRowTemplate to land
// on the final per-location URL — that's a no-op for steps 1 and 2
// since those values don't carry placeholders.

import type { AccountCampaign } from '../types/accountCampaign.js';
import type { FranchiseLocationProfile } from '../types/franchiseLocationProfile.js';
import type { FranchiseProfile } from '../types/franchiseProfile.js';
import {
  flattenInherit,
  resolveMasterLanding,
  resolvePlatformLanding,
} from '../modules/campaigns/landingPage.js';

type RowSubset = Pick<
  AccountCampaign,
  | 'landingPageType'
  | 'customLandingPageUrl'
  | 'metaLandingPageOverride'
  | 'googleLandingPageOverride'
>;

type BrandSubset = Pick<FranchiseProfile, 'landingPageTemplate'> | null;

type LocationSubset = Pick<
  FranchiseLocationProfile,
  'offerLandingPage' | 'saleLandingPage' | 'locationLandingPage'
> | null;

export function resolveRowLandingUrl(
  row: RowSubset,
  brandProfile: BrandSubset,
  locationProfile: LocationSubset,
  platform: 'meta' | 'google',
): string | null {
  const templates = brandProfile?.landingPageTemplate ?? null;
  if (!row.landingPageType) return null;
  const master = resolveMasterLanding(
    row.landingPageType,
    row.customLandingPageUrl,
    templates,
    locationProfile,
  );
  const override =
    platform === 'meta'
      ? row.metaLandingPageOverride
      : row.googleLandingPageOverride;
  const platformResolution = override
    ? resolvePlatformLanding(
        override,
        row.landingPageType,
        row.customLandingPageUrl,
        templates,
        locationProfile,
      )
    : { status: 'inherit' as const };
  const flat = flattenInherit(platformResolution, master);
  return flat.status === 'ok' ? flat.url : null;
}
